import { sepAPI } from '../API';
import {
  UpdateTaskStatusResult,
  UpdateTaskStatusMutationBody,
  TaskSearchResult,
  TaskStatus,
  UpdateMultipleTaskBody,
  UpdateTaskBody,
  Task,
  CreateTaskBody,
  TaskExtended,
  CreateTaskTemplateBody,
  UpdateTaskTemplateBody,
  TaskTemplate,
  TaskDependency,
  CreateTaskDependencyTemplateBody,
  UpdateTaskDependencyTemplateBody,
  UpdateTaskByAdminBody,
  TaskTemplateSLA,
} from '../../../shared/types/Task';
import { SearchParams } from '../../../shared/types/SEP';
import { setSnackbarForEndpoint } from '../../utils/snackbar';

export const tasksSlice = sepAPI.injectEndpoints({
  endpoints: (builder) => ({
    getTasks: builder.query<TaskSearchResult, SearchParams>({
      query: (arg) => {
        const { limit, offset, sortBy, sortAsc, status, assigneeId, search } =
          arg;
        return {
          url: 'tasks',
          params: {
            limit,
            offset,
            sortBy,
            sortAsc,
            status,
            'assignee.id': assigneeId,
            search,
          },
        };
      },
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          errorMessage: 'There was a problem getting the tasks.',
        });
      },
    }),
    getTaskById: builder.query<TaskExtended, number>({
      query: (id) => `task/${id}`,
      providesTags: ['Tasks'],
    }),
    getTasksWithSEPID: builder.query<TaskExtended[], number>({
      query: (id) => {
        return {
          url: `sep/${id}/tasks`,
        };
      },
      providesTags: ['Tasks'],
    }),
    getMultipleSEPsTasks: builder.query<TaskExtended[], number[]>({
      query: (ids) => ({
        url: `tasks/multiple-seps`,
        params: {
          sepIDs: ids.join(','),
        },
      }),
      providesTags: ['Tasks'],
    }),
    getAllTasks: builder.query<TaskSearchResult, void>({
      query: () => {
        return {
          url: 'tasks',
        };
      },
    }),
    updateTaskStatus: builder.mutation<
      UpdateTaskStatusResult,
      UpdateTaskStatusMutationBody
    >({
      query: (arg) => {
        const { status, taskID } = arg;
        return {
          url: `task/${taskID}/status`,
          method: 'PATCH',
          body: {
            status,
          },
        };
      },
      invalidatesTags: ['Tasks', 'TasksForSEP', 'SEPs', 'DefaultStakeholdersForSEP'],
      onQueryStarted: (arg, api) => {
        // Show simple snackbar for status changes
        let successMessage;
        switch (arg.status) {
          case TaskStatus.todo:
            successMessage = 'Task status set to To-do. This task will appear in your To-do list.';
            break;
          case TaskStatus.inReview:
            successMessage = 'Task submitted for review. The reviewer will be notified.';
            break;
          case TaskStatus.changesRequested:
            successMessage = 'Changes have been requested. The assignee will be notified of the required changes.';
            break;
          case TaskStatus.complete:
            successMessage = 'Task has been completed successfully!';
            break;
          default:
            successMessage = 'Task status updated!';
        }
        setSnackbarForEndpoint(api, {
          errorMessage: "There was a problem updating this task's status!",
          successMessage,
        });
      },
    }),
    updateTasks: builder.mutation<
      any,
      { sepId: number; tasks: UpdateMultipleTaskBody[] }
    >({
      query: ({ sepId, tasks }) => ({
        url: `sep/${sepId}/tasks`,
        method: 'PATCH',
        body: tasks,
      }),
      invalidatesTags: ['Tasks', 'TasksForSEP', 'SEPs', 'DefaultStakeholdersForSEP'],
      onQueryStarted: (arg, api) => {
        const taskCount = arg.tasks?.length || 1;
        const successMessage = taskCount === 1
          ? 'Task assignment has been updated. The assignee will be notified.'
          : `${taskCount} task assignments have been updated. Team members will be notified.`;
        setSnackbarForEndpoint(api, {
          successMessage,
          errorMessage: 'There was a problem updating task assignments.',
        });
      },
    }),
    updateTask: builder.mutation<
      Task,
      { taskID: number; task: UpdateTaskBody }
    >({
      query: ({ taskID, task }) => ({
        url: `task/${taskID}`,
        method: 'PATCH',
        body: task,
      }),
      onQueryStarted: (arg, api) => {
        // Action-specific messages with contextual information
        let successMessage = 'Task updated successfully!';
        const changeType = (arg as any).changeType;
        const assigneeName = (arg as any).assigneeName || '';
        const ownerName = (arg as any).ownerName || '';
        const oldOwnerName = (arg as any).oldOwnerName || '';
        const taskName = (arg as any).task?.name || 'This task';
        let snackbarDuration = 6000;

        if (changeType) {
          switch (changeType.toLowerCase()) {
            case 'assignee': {
              successMessage = `Task assigned to ${assigneeName} successfully!`;
              snackbarDuration = 8000;
              break;
            }
            case 'owner': {
              successMessage = `Task ownership transferred from ${oldOwnerName} to ${ownerName}.`;
              snackbarDuration = 12000;
              break;
            }
            case 'state':
              successMessage = `Task status has been updated successfully!`;
              snackbarDuration = 6000;
              break;
            case 'name':
              successMessage = 'Task name updated successfully!';
              snackbarDuration = 6000;
              break;
            default:
              successMessage = 'Task updated successfully!';
              snackbarDuration = 15000;
          }
        } else {
          // If no changeType is passed, use longer duration for owner changes
          if (ownerName) {
            successMessage = `Task ownership transferred from ${oldOwnerName} to ${ownerName}.`;
            snackbarDuration = 18000;
          }
        }

        setSnackbarForEndpoint(api, {
          successMessage,
          errorMessage: 'There was a problem updating task.',
        }, snackbarDuration);
      },
      invalidatesTags: ['TasksForSEP', 'Tasks', 'DefaultStakeholdersForSEP'],
    }),
    createTask: builder.mutation<TaskExtended, CreateTaskBody>({
      query: (body) => ({
        url: `task`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['Tasks', 'TasksForSEP', 'SEPs', 'DefaultStakeholdersForSEP'],
      onQueryStarted: (body: any, api: any) => {
        const taskName = (body as any).name || 'Task';
        setSnackbarForEndpoint(api, {
          successMessage: `Task '${taskName}' has been created successfully!`,
          errorMessage: 'There was a problem creating this task.',
        });
      },
    }),
    getTaskTemplates: builder.query<TaskTemplate[], void>({
      query: () => 'task-templates',
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'TaskTemplate' as const,
                id,
              })),
              'TaskTemplate',
            ]
          : ['TaskTemplate'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          errorMessage: 'There was a problem getting this SEP.',
        });
      },
    }),
    createTaskTemplate: builder.mutation<Task, CreateTaskTemplateBody>({
      query: (body) => ({
        url: 'task-templates',
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['TaskTemplate'],
      onQueryStarted: (body: any, api: any) => {
        const templateName = (body as any).shortName || 'Template';
        setSnackbarForEndpoint(api, {
          successMessage: `Task template '${templateName}' has been created.`,
          errorMessage: 'There was a problem creating this task template.',
        });
      },
    }),
    deleteTaskTemplate: builder.mutation<void, number | { id: number; templateName?: string }>({
      query: (arg) => {
        const id = typeof arg === 'number' ? arg : arg.id;
        return {
          url: `task-template/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => {
        const id = typeof arg === 'number' ? arg : arg.id;
        return [{ type: 'TaskTemplate', id }];
      },
      onQueryStarted: (arg: any, api: any) => {
        const templateName = (typeof arg === 'object' ? arg.templateName : undefined) || 'Template';
        setSnackbarForEndpoint(api, {
          successMessage: `Task template '${templateName}' has been deleted. It will no longer be available for new SEPs.`,
          errorMessage: 'Could not delete task template. Please try again.',
        });
      },
    }),
    updateTaskTemplate: builder.mutation<
      Task,
      { id: number; body: UpdateTaskTemplateBody }
    >({
      query: ({ id, body }) => ({
        url: `task-template/${id}`,
        method: 'PATCH',
        body: body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'TaskTemplate', id: arg.id },
      ],
      onQueryStarted: (arg: any, api: any) => {
        const templateName = (arg?.body as any)?.shortName || (arg as any)?.templateName || 'Template';
        setSnackbarForEndpoint(api, {
          successMessage: `Task template '${templateName}' has been updated successfully!`,
          errorMessage: 'Could not update task template. Please try again.',
        });
      },
    }),
    getTaskTemplatesSLA: builder.query<TaskTemplateSLA[], void>({
      query: () => 'task-templates-sla',
      providesTags: ['Tasks'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          errorMessage: 'There was a problem getting task templates SLA.',
        });
      },
    }),
    addTaskDependency: builder.mutation<
      TaskDependency,
      CreateTaskDependencyTemplateBody
    >({
      query: (body) => ({
          url: 'task-dependency-templates',
          method: 'POST',
        body: body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'TaskTemplate', id: arg.taskID },
      ],
      onQueryStarted: (body, api) => {
        setSnackbarForEndpoint(api, {
          successMessage: 'Task dependency mapping created. This task must be completed before the dependent task can proceed.',
          errorMessage: 'There was a problem creating this task dependency.',
        });
      },
    }),
    updateTaskDependency: builder.mutation<
      TaskDependency,
      { id: number; body: UpdateTaskDependencyTemplateBody }
    >({
      query: ({ id, body }) => ({
          url: `task-dependency-template/${id}`,
          method: 'PATCH',
             body: body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'TaskTemplate', id: arg.body.taskID },
      ],
      onQueryStarted: (body, api) => {
        setSnackbarForEndpoint(api, {
          successMessage: 'Task dependency has been updated. The workflow dependency has been modified.',
          errorMessage: 'There was a problem updating this task dependency.',
        });
      },
    }),
    deleteTaskDependency: builder.mutation<
      void,
      { taskID: number; dependentTaskID: number; id: number }
    >({
      query: ({ id, taskID }) => ({
        url: `task-dependency-template/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'TaskTemplate', id: arg.taskID },
      ],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          successMessage: 'Task dependency has been removed. These tasks are no longer dependent on each other.',
          errorMessage: 'There was a problem deleting this task dependency.',
        });
      },
    }),
    updateTaskByAdmin: builder.mutation<
      any,
      { taskID: number; taskDetails: UpdateTaskByAdminBody; taskName?: string }
    >({
      query: ({ taskID, taskDetails }: { taskID: number; taskDetails: UpdateTaskByAdminBody }) => ({
        url: `task-update/${taskID}`,
        method: 'PATCH',
        body: {
          oldTaskState: taskDetails.oldTaskState,
          newTaskState: taskDetails.newTaskState,
          sepID: taskDetails.sepID
        },
      }),
      onQueryStarted: (arg: any, api: any) => {
        const taskName = arg.taskName || arg.taskDetails?.newTaskState?.taskName || 'Task';
        const shortName = arg.taskDetails?.newTaskState?.shortName || '';
        const displayName = shortName ? `${shortName}` : taskName;
        setSnackbarForEndpoint(api, {
          successMessage: `Task '${displayName}' permissions updated successfully!`,
          errorMessage: 'Could not update task. Please try again.',
        });
      },
      invalidatesTags: ['TasksForSEP', 'Tasks'],
    }),
    exportTaskSLAs: builder.query<any, void>({
      query: () => ({
        url: `task-templates-sla/export/excel`,
        cache: 'no-cache',
        headers: {
          'content-type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseHandler: async (response: any) => {
          return window.URL.createObjectURL(await response.blob());
        },
      }),
      onQueryStarted: (arg: any, api: any) => {
        setSnackbarForEndpoint(api, {
          successMessage: 'Task templates SLA exported successfully!',
          errorMessage: 'Could not export task templates SLA. Please try again.',
        });
      },
    }),
  }),
  // getMultipleSEPsTasks: builder.query<TaskExtended[], number[]>({
  //   query: (ids) => ({
  //     url: `tasks/multiple-seps`,
  //     params: {
  //       ids: ids.join(','),
  //     },
  //   }),
  //   providesTags: ['Tasks'],
  // }),
});

export const {
  useGetTasksQuery,
  useGetTaskByIdQuery,
  useLazyGetTaskByIdQuery,
  useGetTasksWithSEPIDQuery,
  useUpdateTaskStatusMutation,
  useGetAllTasksQuery,
  useUpdateTasksMutation,
  useUpdateTaskMutation,
  useCreateTaskMutation,
  useGetTaskTemplatesQuery,
  useCreateTaskTemplateMutation,
  useDeleteTaskTemplateMutation,
  useAddTaskDependencyMutation,
  useDeleteTaskDependencyMutation,
  useUpdateTaskTemplateMutation,
  useUpdateTaskDependencyMutation,
  useUpdateTaskByAdminMutation,
  useGetMultipleSEPsTasksQuery,
  useGetTaskTemplatesSLAQuery,
  useLazyExportTaskSLAsQuery,
  
} = tasksSlice;
